import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule, registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ModuleWithProviders, NgModule, LOCALE_ID } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { MatSelectModule } from '@angular/material/select';
import { MiqImagesModule, MiqSecuredResourcesModule, TranslationModule } from '@miq/utils';
import { TableModule } from 'primeng/table';
import { AbstractDynamicReportViewComponent } from './abstract/abstract-dynamic-report-view.component';
import { MinLeasedTermService } from './custom-data/min-leased-term.service';
import { OfferedSpaceService } from './custom-data/offered-space.service';
import { ResearchAvailabilityService } from './custom-data/research-availability.service';
import { ResearchDevelopmentPipelineService } from './custom-data/research-development-pipeline.service';
import { InterpolatedBlueprintService } from './interpolated-blueprint.service';
import { InterpolationFillerService } from './interpolation-filler.service';
import { InterpolationResolverService } from './interpolation-resolver.service';
import { MapAndSetInterpolationService } from './map-and-set-interpolation.service';
import { ReportBluePrintInterpolationService } from './report-blueprint-interpolation.service';
import { ReportExportViewService } from './report-export-view.service';
import {
  INFO_USER_RESOLVER_TOKEN,
  REPORT_EXPORT_CONFIGS_TOKEN,
  ReportExportConfigurations,
} from './report-export-view.token';
import { ReportFieldFormatterService } from './report-field-formatter.service';
import { PropertyGroupService } from './report-group.service';
import { ReportImagesService } from './report-images.service';
import { GetDateString } from './shared/pipes/date.pipe';
import { IsFieldVisiblePipe } from './shared/pipes/field-visible.pipe';
import { LabelParserPipe } from './shared/pipes/label-parser.pipe';
import { ReportFieldFormatterPipe } from './shared/pipes/report-field-formatter.pipe';
import { ReportFieldRouteFnPipe } from './shared/pipes/report-field-route-fn.pipe';
import { ReportLabelFormatterPipe } from './shared/pipes/report-label-formatter.pipe';
import { ReportRecordRouteFnPipe } from './shared/pipes/report-record-route-fn.pipe';
import { SelectTemplateOutletPipe } from './shared/pipes/select-template-outlet.pipe';
import { SelectTemplateDialogComponent } from './shared/select-template-dialog/select-template-dialog.component';
import { TemplateDynamicService } from './template-dynamic.service';
import { CompareActionsToolbarComponent } from './views/comparison-view/compare-actions-toolbar/compare-actions-toolbar.component';
import { CompareRecordBodyComponent } from './views/comparison-view/compare-record-body/compare-record-body.component';
import { CompareRecordHeaderComponent } from './views/comparison-view/compare-record-header/compare-record-header.component';
import { ComparisonViewComponent } from './views/comparison-view/comparison-view.component';
import { CondensedBodyComponent } from './views/condensed-view/condensed-body/condensed-body.component';
import { CondensedHeaderComponent } from './views/condensed-view/condensed-header/condensed-header.component';
import { CondensedRecordDataPipe } from './views/condensed-view/condensed-view-record.pipe';
import { CondensedViewComponent } from './views/condensed-view/condensed-view.component';
import { DisplayFieldPipe } from './views/condensed-view/display.pipe';
import { DataViewFormatterPipe } from './views/data-view/data-view-formatter.pipe';
import { DataViewRoutePipe } from './views/data-view/data-view-route.pipe';
import { DataViewTableComponent } from './views/data-view/data-view-table/data-view-table.component';
import { DataViewToolbarComponent } from './views/data-view/data-view-toolbar/data-view-toolbar.component';
import { DataViewComponent } from './views/data-view/data-view.component';
import { DevelopmentPipelineViewComponent } from './views/development-pipeline-view/development-pipeline-view.component';
import { DevelopmentPipelineViewImageComponent } from './views/development-pipeline-view/rep-development-pipeline-view-image/development-pipeline-view-image.component';
import { DynamicContactsComponent } from './views/presentation-dynamic-view/dynamic-contacts/dynamic-contacts.component';
import { GetContactInitialsPipe } from './views/presentation-dynamic-view/dynamic-contacts/get-contact-initials.pipe';
import { GetInitialColorPipe } from './views/presentation-dynamic-view/dynamic-contacts/initial-color.pipe';
import { SelectContactsDialogComponent } from './views/presentation-dynamic-view/dynamic-contacts/select-contacts/select-contacts.component';
import { PresentationDynamicViewComponent } from './views/presentation-dynamic-view/presentation-dynamic-view.component';
import { PresentationDynamicViewBodyComponent } from './views/presentation-dynamic-view/record-body/record-body.component';
import { PresentationDynamicViewHeaderComponent } from './views/presentation-dynamic-view/record-body/record-header/record-header.component';
import { PresentationDynamicViewImageComponent } from './views/presentation-dynamic-view/record-body/record-image/record-image.component';
import { ReportResetImagesService } from './views/presentation-dynamic-view/record-body/record-image/report-reset-images.service';
import { ActionsToolbarComponent } from './views/presentation-view/actions-toolbar/actions-toolbar.component';
import { PresentationViewComponent } from './views/presentation-view/presentation-view.component';
import { FloorPlanComponent } from './views/presentation-view/record-body/floor-plan/floor-plan.component';
import { RecordBodyComponent } from './views/presentation-view/record-body/record-body.component';
import { RecordFloorPlanComponent } from './views/presentation-view/record-body/record-floor-plan/record-floor-plan.component';
import { RecordHeaderComponent } from './views/presentation-view/record-body/record-header/record-header.component';
import { RecordImageComponent } from './views/presentation-view/record-body/record-image/record-image.component';
import { SummaryViewComponent } from './views/summary-view/summary-view.component';
import { TableInfoComponent } from './views/table-view/table-info/table-info.component';
import { HighlightDatePipe } from './views/table-view/table-info/table-info.pipe';
import { TableViewComponent } from './views/table-view/table-view.component';
import { TagOutputViewComponent } from './views/tag-output-view/tag-output-view.component';
import { L10nTranslationModule } from 'angular-l10n';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { ContactSettingsComponent } from './views/presentation-dynamic-view/dynamic-contacts/contact-settings/contact-settings.component';
import { ReportContactsService } from './custom-data/report-contacts.service';
import localeIt from '@angular/common/locales/it';
import localeNl from '@angular/common/locales/nl';
import { ExtractRecordsFromReportRecordFnPipe } from './shared/pipes/extract-records-from-report-record-fn.pipe';
import { SharedPipesModule } from '@miq/utils/pipes';

@NgModule({
  declarations: [
    SelectTemplateDialogComponent,
    SelectContactsDialogComponent,
    ActionsToolbarComponent,
    PresentationViewComponent,
    SummaryViewComponent,
    TableViewComponent,
    TableInfoComponent,
    HighlightDatePipe,
    GetContactInitialsPipe,
    GetInitialColorPipe,
    RecordHeaderComponent,
    RecordBodyComponent,
    RecordImageComponent,
    RecordFloorPlanComponent,
    ReportFieldFormatterPipe,
    ReportLabelFormatterPipe,
    AbstractDynamicReportViewComponent,
    ComparisonViewComponent,
    CompareRecordHeaderComponent,
    CompareRecordBodyComponent,
    CompareActionsToolbarComponent,
    CondensedViewComponent,
    CondensedHeaderComponent,
    CondensedBodyComponent,
    SelectTemplateOutletPipe,
    ReportFieldRouteFnPipe,
    ReportRecordRouteFnPipe,
    DisplayFieldPipe,
    PresentationDynamicViewComponent,
    DynamicContactsComponent,
    ContactSettingsComponent,
    PresentationDynamicViewHeaderComponent,
    PresentationDynamicViewBodyComponent,
    PresentationDynamicViewImageComponent,
    IsFieldVisiblePipe,
    CondensedRecordDataPipe,
    LabelParserPipe,
    DataViewComponent,
    DataViewToolbarComponent,
    DataViewTableComponent,
    DataViewFormatterPipe,
    DataViewRoutePipe,
    DevelopmentPipelineViewComponent,
    DevelopmentPipelineViewImageComponent,
    TagOutputViewComponent,
    FloorPlanComponent,
    GetDateString,
    ExtractRecordsFromReportRecordFnPipe,
  ],
  exports: [
    ReportFieldFormatterPipe,
    ReportLabelFormatterPipe,
    PresentationViewComponent,
    TableViewComponent,
    ComparisonViewComponent,
    CondensedViewComponent,
    PresentationDynamicViewComponent,
    SummaryViewComponent,
    IsFieldVisiblePipe,
    LabelParserPipe,
    DataViewComponent,
    DataViewFormatterPipe,
    DataViewRoutePipe,
    DevelopmentPipelineViewComponent,
    TagOutputViewComponent,
    FloorPlanComponent,
    GetDateString,
    ExtractRecordsFromReportRecordFnPipe,
  ],
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatDialogModule,
    MatSnackBarModule,
    MatButtonModule,
    MatRadioModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule.withConfig({
      callSetDisabledState: 'whenDisabledForLegacyCode',
    }),
    MiqSecuredResourcesModule,
    TranslationModule,
    MiqImagesModule,
    TableModule,
    DragDropModule,
    MatTooltipModule,
    MatTableModule,
    RouterModule,
    L10nTranslationModule,
    MatSelectModule,
    OverlayPanelModule,
    SharedPipesModule,
  ],
  providers: [
    ReportExportViewService,
    InterpolatedBlueprintService,
    ReportImagesService,
    PropertyGroupService,
    ReportFieldFormatterService,
    TemplateDynamicService,
    InterpolationFillerService,
    MapAndSetInterpolationService,
    InterpolationResolverService,
    MinLeasedTermService,
    OfferedSpaceService,
    ReportResetImagesService,
    ReportBluePrintInterpolationService,
    ResearchAvailabilityService,
    ResearchDevelopmentPipelineService,
    ReportContactsService,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class ReportExportViewModule {
  /**
   * Receives a {@link } object and injects it into
   * the module as a provider using the {@link } as
   * the injection token.
   */
  static forRoot(
    options: ReportExportConfigurations,
    userInfoResolver: any
  ): ModuleWithProviders<ReportExportViewModule> {
    return {
      ngModule: ReportExportViewModule,
      providers: [
        { provide: REPORT_EXPORT_CONFIGS_TOKEN, useValue: options },
        { provide: LOCALE_ID, useValue: ['it-IT', 'nl-NL'] },
        { provide: INFO_USER_RESOLVER_TOKEN, useClass: userInfoResolver },
      ],
    };
  }
  constructor() {
    registerLocaleData(localeIt);
    registerLocaleData(localeNl);
  }
}
